<template>
  <div class="doc-content">
    <div class="app-header">
      <h1>Babykurse in Koblenz: Laufende Angebote, Krabbelgruppen und vieles mehr</h1>
      <p>Hier findet ihr alle Krabbelgruppen, Babyschwimmen, Sportkurse für Schwangere und weitere Babykurse in Koblenz und Umgebung – von Eltern für Eltern gepflegt.</p>
    </div>
    
    <div v-if="loading" class="info-message loading">
      Daten werden geladen...
    </div>
    
    <div v-else-if="error" class="info-message error">
      Fehler beim Laden der Daten: {{ error }}
    </div>
    
    <div v-else>
      <!-- Table Wrapper für horizontales Scrollen auf kleinen Geräten -->
      <!-- Dieses Element übernimmt das Scrollen -->
      <div class="table-wrapper">
        <!-- Jede Kategorie wird zum scrollbaren Inhalt, indem sie die Breite der Tabelle annimmt -->
        <div v-for="categoryData in content" :key="categoryData.category" class="category-section">
          <h2 class="category-title">{{ categoryData.category }}</h2>
          
          <div v-if="categoryData.events.length === 0" class="no-events">
            Aktuell keine Angebote in dieser Kategorie.
          </div>
          
          <table v-else class="data-table">
            <thead>
              <tr>
                <th v-for="header in getHeaders(categoryData.events)" :key="header">{{ header }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(event, index) in categoryData.events" :key="index">
                <td v-for="header in getHeaders(categoryData.events)" :key="header">
                  {{ event[header] || '-' }}
                </td>
              </tr>
            </tbody>
          </table>
        
        </div>
      </div>
    </div>
    
    <hr class="form-separator">
    <div class="table-wrapper">
      <section
        ref="addEventSection"
        class="add-content-section"
      >
        <h2>➕ Neue Inhalte hinzufügen</h2>
        
        <form @submit.prevent="submitNewContent" class="content-form">
          <div class="form-group">
            <label for="category-select">Wähle Kategorie oder erstelle neue:</label>
            <select id="category-select" v-model="form.selectedCategory" @change="handleCategoryChange">
              <option value="">-- Wähle eine Kategorie --</option>
              <option v-for="cat in categories" :key="cat.category" :value="cat.category">{{ cat.category }}</option>
              <option value="new">-- Neue Kategorie erstellen --</option>
            </select>
          </div>
          
          <div v-if="form.selectedCategory === 'new'" class="form-group new-category-group">
            <label for="new-cat-name">Name der neuen Kategorie (Überschrift):</label>
            <input type="text" id="new-cat-name" v-model="form.newCategoryName" required>
          </div>
          
          <div class="dynamic-fields-section">
            <p class="field-info">Gib hier die Details deines Eintrags ein:</p>
            <div v-for="(col, index) in form.columns" :key="index" class="dynamic-field-row">
              
              <div class="input-container">
                <!-- Spaltentitel-Input (nur bei neuer Kategorie) -->
                <input 
                  v-if="showAddColumnButton" 
                  type="text" 
                  v-model="col.header" 
                  :placeholder="'Spaltentitel ' + (index + 1)" 
                  class="header-input" 
                  required
                >
                <!-- Spaltentitel-Label (bei bestehender Kategorie) -->
                <label v-else class="header-label">{{ col.header }}:</label>
        
                <!-- Wert-Input -->
                <input 
                  type="text" 
                  v-model="col.value" 
                  :placeholder="'Inhalt für ' + col.header" 
                  class="value-input" 
                  :required="index < 1"
                >
              </div>
              
              <!-- Entfernen-Button (nur bei neuer Kategorie und ab der zweiten Spalte) -->
              <button 
                v-if="showAddColumnButton && index > 0" 
                type="button" 
                @click="removeColumn(index)" 
                class="remove-col-btn"
              >
                &times;
              </button>
            </div>
            
            <button 
              v-if="showAddColumnButton" 
              type="button" 
              @click="addColumn" 
              class="add-col-btn"
            >
              + Weitere Spalte hinzufügen
            </button>
          </div>
          
          <div class="form-group honeypot-field">
            <label for="honeypot">Bitte nicht ausfüllen:</label>
            <input type="text" id="honeypot" v-model="form.honeypot">
          </div>
          
          <button type="submit" :disabled="isSubmitting" class="cta-button">
            {{ isSubmitting ? 'Wird hinzugefügt...' : 'Eintrag senden' }}
          </button>
        
          <div v-if="submitStatus" :class="['status-message', submitStatus.success ? 'success' : 'error']">
            {{ submitStatus.message }}
          </div>
        </form>
      </section>
    </div>
    
    <hr class="form-separator">
    <div class="disclaimer">
      <h3>Hinweis zur Liste</h3>
      <p>
          Diese Liste von Kursen und fortlaufenden Angeboten ist ein Service für die WhatsApp-Community "Koblenz mit Baby"
          und wird aktiv von Euch, den Community-Mitgliedern, gepflegt und ergänzt.
      </p>
      <p>
          Ich übernehme keine Gewähr für die Aktualität, Vollständigkeit,
          Richtigkeit oder inhaltliche Zulässigkeit der hier eingetragenen Angebote, da diese von Dritten stammen. 
          Die Verantwortung für die Inhalte der jeweiligen Veranstaltung liegt bei dem Community-Mitglied, das sie eingetragen hat.
      </p>
      <p>
          Sollten Euch Fehler, unvollständige Angaben oder rechtswidrige Inhalte auffallen, bitte ich Euch dringend um einen
          umgehenden Hinweis an <router-link @click.native="closeMenu" to="/kontakt/">mich</router-link>.
      </p>
    </div>
    
    <button
      class="floating-action-btn"
      @click="handleFloatingButton"
      :aria-label="showScrollTop ? 'Nach oben' : 'Neue Veranstaltung hinzufügen'"
    >
      <span v-if="showScrollTop">⬆️</span>
      <span v-else>➕</span>
    </button>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
// 1. Statische Daten importieren (werden beim Build von GitHub erstellt)
import staticCourses from '@/data/courses-static.json';

const SECRET_KEY = 'MeinGanzGeheimerSchluesselFuerKO'; 
const API_URL = 'https://script.google.com/macros/s/AKfycbxZYS6HB_AHIYJahlTVBakcE0IEw8tBRoiwAbljvs9Dfi2qayPsrkNM43YsGoCh_XQ9/exec'; 

const content = ref(staticCourses.content || []); // Für die Anzeige der Daten
const categories = ref(staticCourses.categories || []); // Nur die Kategorien und deren Header
const loading = ref(false);
const error = ref(null);
const addEventSection = ref(null);
const showScrollTop = ref(false);

const isSubmitting = ref(false);
const submitStatus = ref(null);

const form = ref({
  selectedCategory: '',
  newCategoryName: '',
  columns: [],
  honeypot: '', // Das Honeypot-Feld
});


const getHeaders = (events) => {
  if (!events || events.length === 0) return [];
  const allKeys = events.flatMap(event => Object.keys(event));
  return [...new Set(allKeys)]; 
};

const getDefaultColumns = (headers = []) => {
  // Beachte: Im AppsScript-Code wird keine Spalte als Pflichtfeld behandelt.
  // Hier wird nur die Struktur für die Eingabe bereitgestellt.
  return headers.map(h => ({ header: h, value: '' }));
};

const fetchData = async () => {
  try {
    const response = await fetch(API_URL, { redirect: 'follow' });
    
    if (!response.ok) {
      if (response.status === 429) {
        throw new Error('Zu viele Anfragen. Bitte später erneut versuchen.');
      } else if (response.status === 503) {
        throw new Error('Dienst vorübergehend nicht verfügbar.');
      } else {
        throw new Error(`HTTP-Fehler! Status: ${response.status}`);
      }
    }
    
    const data = await response.json();
    content.value = data.content || [];
    categories.value = data.categories || [];
    
    // Initialisiere Formular mit Standard-Spalten (z.B. Name, Ort)
    form.value.columns = getDefaultColumns();
    
  } catch (err) {
    console.error("API Fehler, nutze statische Daten:", err);
    if (err.message.includes('Failed to fetch')) {
      error.value = 'Keine Internetverbindung. Bitte überprüfen Sie Ihre Verbindung.';
    } else {
      error.value = err.message || 'Fehler beim Laden der Daten.';
    }
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true });
  fetchData();
});

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll);
});

// ********** Formular-Logik **********
const showAddColumnButton = computed(() => {
  // 1. IMMER anzeigen, wenn es sich um eine neue Kategorie handelt
  if (form.value.selectedCategory === 'new') {
    // Zusätzlich prüfen, dass max. 10 Spalten nicht überschritten werden
    return form.value.columns.length < 10;
  }

  // 2. Anzeigen, wenn eine BESTEHENDE Kategorie gewählt wurde
  if (form.value.selectedCategory && form.value.selectedCategory !== 'new') {
    const selectedCat = categories.value.find(c => c.category === form.value.selectedCategory);
    
    // Prüft, ob die Kategorie in den Metadaten existiert und KEINE Header hat (also leer ist)
    // Wenn sie leer ist, MUSS der Benutzer Spalten hinzufügen, um den ersten Eintrag zu erstellen.
    if (selectedCat && selectedCat.headers.length === 0) {
      return form.value.columns.length < 10;
    }
  }

  // Ansonsten Button ausblenden
  return false;
});

// Ändert die Spalten, wenn eine Kategorie gewählt wird
const handleCategoryChange = () => {
  // Zurücksetzen aller Werte im Formular
  form.value.newCategoryName = '';
  form.value.columns = [];

  if (form.value.selectedCategory === 'new') {
    // Neue Kategorie: Setze auf Standard-Spalten, die änderbar sind.
    form.value.columns = getDefaultColumns(["Name / Anbieter*in", "Ort", "Tag", "Uhrzeit", "Kosten", "Sonstiges"]);
  } else if (form.value.selectedCategory) {
    // Bestehende Kategorie: Setze die Spalten auf die Header der ersten Tabelle
    const selectedCat = categories.value.find(c => c.category === form.value.selectedCategory);
    if (selectedCat && selectedCat.headers.length > 0) {
      form.value.columns = getDefaultColumns(selectedCat.headers);
    } else {
      // Fallback, wenn Kategorie noch keine Einträge/Header enthält
      form.value.columns = getDefaultColumns(["Name / Anbieter*in", "Ort", "Tag", "Uhrzeit", "Kosten", "Sonstiges"]); 
    }
  }
};

// Fügt eine neue Spalte hinzu (nur bei neuer Kategorie)
const addColumn = () => {
  if (form.value.columns.length < 10) {
    form.value.columns.push({ header: `Spalte ${form.value.columns.length + 1}`, value: '' });
  }
};

// Entfernt eine Spalte (nur bei neuer Kategorie)
const removeColumn = (index) => {
  // Sicherstellen, dass mindestens eine Spalte verbleibt
  if (form.value.columns.length > 1) {
    form.value.columns.splice(index, 1);
  }
};


// Sendet das Formular an das Apps Script
const submitNewContent = async () => {
  isSubmitting.value = true;
  submitStatus.value = null;

  if (form.value.selectedCategory === 'new' && !form.value.newCategoryName.trim()) {
      submitStatus.value = { success: false, message: 'Bitte gib einen Namen für die neue Kategorie ein.' };
      isSubmitting.value = false;
      return;
  }
  
  // Überprüfung, ob bei neuer Kategorie mindestens ein Header ausgefüllt ist
  if (form.value.selectedCategory === 'new' && form.value.columns.some(c => !c.header.trim())) {
      submitStatus.value = { success: false, message: 'Bitte fülle alle Spaltentitel für die neue Kategorie aus.' };
      isSubmitting.value = false;
      return;
  }

  // Daten für den POST-Request vorbereiten
  const rowData = form.value.columns.reduce((acc, col) => {
    // Verwende den Header als Schlüssel und den Value als Wert
    acc[col.header] = col.value; 
    return acc;
  }, {});

  const postData = {
    key: SECRET_KEY, // Geheimschlüssel für die Sicherheit
    honeypot: form.value.honeypot, // Honeypot-Feld (sollte leer sein)
    category: form.value.selectedCategory,
    isNewCategory: form.value.selectedCategory === 'new',
    newCategoryName: form.value.newCategoryName.trim(),
    columnHeaders: form.value.columns.map(c => c.header), // Nur die Header bei neuer Kategorie
    rowData: rowData // Die eigentlichen Daten
  };
  
  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain;charset=UTF-8' // Wichtig für Apps Script
      },
      body: JSON.stringify(postData)
    });
    
    // Apps Script gibt JSON zurück, auch bei Fehlern
    const result = await response.json(); 

    if (result.success) {
      submitStatus.value = { success: true, message: '✅ Eintrag erfolgreich hinzugefügt! Die Seite wird neu geladen, um die Änderungen anzuzeigen.' };
      // Nach erfolgreichem Speichern kurz warten und die Seite neu laden, um die aktualisierten Daten anzuzeigen
      setTimeout(() => window.location.reload(), 3000); 
    } else {
      submitStatus.value = { success: false, message: `❌ Fehler: ${result.message}` };
    }

  } catch (error) {
    console.error("Fehler beim Senden des Formulars:", error);
    submitStatus.value = { success: false, message: '❌ Netzwerk- oder Serverfehler.' };
  } finally {
    isSubmitting.value = false;
  }
};

const onScroll = () => {
  if (!addEventSection.value) return;

  const rect = addEventSection.value.getBoundingClientRect();

  // Formular sichtbar → Button wird "nach oben"
  showScrollTop.value = rect.top < window.innerHeight * 0.6;
};

const handleFloatingButton = () => {
  if (showScrollTop.value) {
    // Nach oben
    window.scrollTo({ top: 0, behavior: 'smooth' });
  } else {
    // Zum Formular
    addEventSection.value.scrollIntoView({ behavior: 'smooth' });
  }
};
</script>

<style scoped>
.doc-content {
  max-width: none;
  margin: 0 auto;
  padding: 10px; /* Reduzierter Padding für Mobile */
  box-sizing: border-box;
}

@media (min-width: 600px) {
  .doc-content {
    padding: 20px;
  }
}

.app-header h1 {
  font-size: 1.8em;
  color: var(--font-color);
  text-align: center;
  padding-bottom: 15px; 
}
.app-header p {
  text-align: center;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--primary-color);
}

.category-section {
  display: inline-block;
  min-width: 90%;
  margin-bottom: 40px;
  padding: 20px;
  border-left: 5px solid var(--primary-color);
  border-radius: 0 8px 8px 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); /* Etwas stärkerer Schatten */
  background-color: #ffffff;
}

.category-title {
  color: var(--font-color);
  margin-top: 0;
  padding-bottom: 5px;
  border-bottom: 1px solid #eee;
}

/* 1. LÖSUNG: Tabellen-Wrapper für horizontales Scrollen */
.table-wrapper {
  width: 100%;
  overflow-x: auto; /* Schlüssel, damit dieser Bereich scrollt */
  margin-top: 15px;
}

.data-table {
  border-collapse: collapse;
  font-size: 0.9em;
}

.data-table th, .data-table td {
  border: 1px solid #ddd;
  padding: 10px 8px;
  text-align: left;
}

.data-table thead th {
  background-color: var(--tertiary-color);
  font-weight: 700;
  color: var(--font-color);
}

.data-table tbody tr:nth-child(even) {
  background-color: #f9f9f9;
}

/* --- Formular Bereich --- */

.add-content-section {
  display: inline-block;
  min-width: 90%;
  padding: 20px;
  background-color: var(--main-color-2);
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}

.content-form {
  display: grid;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-weight: 600;
  margin-bottom: 5px;
  color: var(--font-color);
}

.form-group input[type="text"], .form-group select {
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 1em;
  /* NEU: Wichtig, um sicherzustellen, dass Inputs nicht über den Container hinausragen */
  box-sizing: border-box; 
  width: 100%; 
}

/* Dynamische Felder */
.dynamic-fields-section {
  padding: 15px;
  border: 2px dashed var(--primary-color);
  border-radius: 8px;
  background-color: var(--secondary-color);
}

.dynamic-field-row {
  display: flex;
  align-items: flex-start; /* Stellt sicher, dass das X oben zentriert ist */
  gap: 10px;
  margin-bottom: 10px;
}

.input-container {
  display: flex;
  gap: 10px;
  flex-grow: 1;
  /* Wichtig, um Überlauf in Flexbox zu verhindern */
  min-width: 0; 
}

.header-input {
  flex: 1;
  font-weight: bold;
  min-width: 0;
}

.header-label {
  flex: 1;
  font-weight: bold;
  line-height: 2.5; 
  margin: 0;
  /* Verhindert, dass das Label den Container sprengt */
  overflow: hidden; 
  white-space: nowrap; 
  text-overflow: ellipsis;
  min-width: 0;
}

.value-input {
  flex: 2;
  min-width: 0;
}

.remove-col-btn {
  background-color: #e74c3c; /* Rot beibehalten für Löschaktion */
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s;
  flex-shrink: 0; /* Wichtig: Button soll nicht schrumpfen */
  font-size: 1.1em;
}

.remove-col-btn:hover {
  background-color: #c0392b;
}

.add-col-btn {
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 10px;
  transition: background-color 0.2s, transform 0.1s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.add-col-btn:hover {
  background-color: var(--button-hover);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.cta-button {
  background-color: var(--button-color);
  color: white;
  padding: 14px 20px;
  border: none;
  border-radius: 6px;
  font-size: 1.1em;
  cursor: pointer;
  transition: background-color 0.2s, box-shadow 0.2s;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.cta-button:hover:not(:disabled) {
  background-color: var(--button-hover);
  box-shadow: 0 6px 10px rgba(0, 0, 0, 0.2);
}

.cta-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
  box-shadow: none;
}

/* Honeypot verstecken */
.honeypot-field {
  position: absolute;
  left: -9999px;
  visibility: hidden;
}

/* Statusmeldungen */
.status-message {
  padding: 10px;
  border-radius: 6px;
  margin-top: 15px;
  font-weight: bold;
}

/* Statusfarben für Usability beibehalten (Grün/Rot) */
.status-message.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.status-message.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.no-events {
    font-style: italic;
    color: #666;
    padding: 10px 0;
}



.floating-action-btn {
  position: fixed;
  right: 16px;
  bottom: 16px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background-color: var(--button-color);
  color: white;
  font-size: 1.6em;
  cursor: pointer;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  transition: transform 0.2s ease, background-color 0.2s ease;
}

.floating-action-btn:hover {
  transform: scale(1.05);
  background-color: var(--button-hover);
}


</style>